import type { MapDocument, ValidationIssue } from "daedalus-core";
import { ARRIVAL_POINT } from "./type-names.ts";

/**
 * The issues of a map's arrival points (ADR-122 decision 2.2; its note "how P1.4-T136 is built", item 3): a name
 * is required and unique on its map, and every room holds at least one. A door or a host names an arrival point's
 * id, never its name, so a name is the builder's word for it and two maps may use the same one.
 */

/**
 * The most characters an arrival point's name holds.
 */
export const ARRIVAL_NAME_MOST = 48;

/**
 * The arrival point issues of one map: each blank name, each name a second arrival point on the map repeats,
 * and a room holding none.
 *
 * @param map The map.
 * @returns The issues.
 */
export function arrivalIssues(map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Set<string>();
  let held = 0;
  for (const layer of map.layers) {
    if (layer.kind !== "entity") {
      continue;
    }
    for (const entity of layer.entities) {
      if (entity.type !== ARRIVAL_POINT) {
        continue;
      }
      held += 1;
      const name = typeof entity.fields["name"] === "string" ? entity.fields["name"].trim() : "";
      if (name === "") {
        issues.push({ mapId: map.id, layerId: layer.id, entityId: entity.id, kind: "arrival.no-name", message: `Arrival point ${entity.id} has no name; name it so doors can be pointed at it` });
      } else if (seen.has(name)) {
        issues.push({ mapId: map.id, layerId: layer.id, entityId: entity.id, kind: "arrival.duplicate-name", message: `Arrival point ${entity.id} is named ${name}, as another on ${map.name} is; give each its own name` });
      }
      seen.add(name);
    }
  }
  if (map.host !== undefined && held === 0) {
    issues.push({ mapId: map.id, kind: "arrival.room-has-none", message: `Room ${map.name} has no Arrival point; place one where people come in` });
  }
  return issues;
}
